/**
 * SOFSEK — Base Tables lookup maintenance (§17.B2/B3).
 * All four Phase 1 lookups (distribution mode, subject type, connection status,
 * org node type) share the identical shape: code + sort_order + is_active + _tl
 * name. One generic CRUD component, four thin page exports — avoids repeating
 * the same list/modal four times over for genuinely identical shapes.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { useActiveLocale } from '../../lib/use_active_locale';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const ACTIVE_LOCALES = ['EN_US', 'ES_MX', 'EN_GB'];
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface LookupRow {
  code: string;
  sort_order: number;
  is_active: 'Y' | 'N';
  tl: { locale_code: string; display_text: string }[];
}

interface LookupConfig {
  pageCode: string;
  table: string;
  tlTable: string;
  codeColumn: string;
  titleKey: string;
  titleDefault: string;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'code', header: 'Code', defaultWidth: 160 },
  { key: 'display_text', header: 'Name', defaultWidth: 260 },
  { key: 'sort_order', header: 'Sort', defaultWidth: 80 },
  { key: 'is_active', header: 'Active', defaultWidth: 90 },
];

function tlName(tl: LookupRow['tl'], loc: string): string {
  return tl.find(x => x.locale_code === loc)?.display_text ?? tl.find(x => x.locale_code === 'EN_US')?.display_text ?? '';
}

function LookupInner({ session, cfg }: { session: AuthSession; cfg: LookupConfig }) {
  // §6/§17, once for every lookup screen this factory produces — the verb is
  // read from cfg.pageCode, so a new lookup page inherits the gate for free.
  const canAdd    = hasAccess(session, cfg.pageCode, 'add');
  const canChange = hasAccess(session, cfg.pageCode, 'change');
  const { t } = useTranslation();
  const locale = useActiveLocale();
  const [rows, setRows] = useState<LookupRow[]>([]);
  const [filter, setFilter] = useState('');
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<LookupRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb
        .from(cfg.table)
        .select(`${cfg.codeColumn}, sort_order, is_active, ${cfg.tlTable} ( locale_code, display_text )`)
        .order('sort_order');
      if (e) throw e;
      const mapped = ((data ?? []) as unknown as Record<string, unknown>[]).map(r => ({
        code: r[cfg.codeColumn] as string,
        sort_order: r.sort_order as number,
        is_active: r.is_active as 'Y' | 'N',
        tl: (r[cfg.tlTable] ?? []) as LookupRow['tl'],
      }));
      setRows(mapped);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [cfg.table]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.code.toLowerCase().includes(q) || tlName(r.tl, locale).toLowerCase().includes(q)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active)));
  }, [rows, filter, activeFacet, locale]);

  const detail = useExpandedRows<string>();
  // §17.B13 — a lookup code's related records are its per-locale translations (§8).
  const buildRelatedGroups = (r: LookupRow): RelatedChildGroup[] => [
    { key: 'translations', title: t('sfk.lookup.related.translations', 'Translations'),
      items: r.tl.map(x => ({ id: x.locale_code, label: `${x.locale_code} — ${x.display_text}`, title: x.display_text })) },
  ];
  const hasRelated = (r: LookupRow) => r.tl.length > 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t(cfg.titleKey, cfg.titleDefault)}
          description={t('sfk.lookup.description', 'Reference codes maintained here, with a translation per active locale.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('common.new', 'New')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: rows.length, label: t('common.total', 'Total') },
          { value: rows.filter(r => r.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('common.boolean.active', 'Active')}
              options={[{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }]}
              selected={activeFacet} onChange={setActiveFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || activeFacet.length > 0}
              onClear={() => { setFilter(''); setActiveFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.code}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.code)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.code)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'code' && <span className="font-mono text-csm-sm">{r.code}</span>} {/* fk-display-ok: own code, this page maintains the lookup itself */}
                        {col.key === 'display_text' && tlName(r.tl, locale)}
                        {col.key === 'sort_order' && r.sort_order}
                        {col.key === 'is_active' && <YesNoBadge value={r.is_active} />}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('common.edit', 'Edit')}</button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.code) && hasRelated(r) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <LookupModal session={session} cfg={cfg} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function LookupModal({ session, cfg, row, onClose, onSaved }: {
  session: AuthSession; cfg: LookupConfig; row: LookupRow | null; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.code ?? '');
  const [sortOrder, setSortOrder] = useState(String(row?.sort_order ?? 0));
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(
    row?.tl ?? ACTIVE_LOCALES.map(loc => ({ locale_code: loc, display_text: '' }))
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!code.trim()) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (!translations.some(tr => tr.display_text?.trim())) throw new Error(t('common.err.translation_required', 'At least one translation is required.'));

      const upperCode = code.trim().toUpperCase();
      if (row === null) {
        const { error: e } = await routedInsert(session, cfg.table, {
          [cfg.codeColumn]: upperCode,
          sort_order: Number(sortOrder),
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      } else {
        const { error: e } = await routedUpdate(session, cfg.table, cfg.codeColumn, row.code, {
          sort_order: Number(sortOrder),
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
      }
      await Promise.all(
        translations.filter(tr => tr.display_text?.trim()).map(tr =>
          routedInsert(session, cfg.tlTable, {
            [cfg.codeColumn]: upperCode,
            locale_code: tr.locale_code,
            display_text: tr.display_text,
          }))
      );
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={isCreate ? t('common.new_record', 'New Record') : `${t('common.edit', 'Edit')} — ${row?.code ?? ''}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input value={code} disabled={!isCreate} onChange={e => setCode(e.target.value.toUpperCase())} className={`${inputCls} font-mono`} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="sort_order" label={t('common.sort_order', 'Sort Order')}>
        <label className={labelCls}>{t('common.sort_order', 'Sort Order')}</label>
        <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} className={inputCls} />
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor availableLocales={ACTIVE_LOCALES} value={translations} onChange={setTranslations} label={t('common.translations', 'Translations')} />

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

function makeLookupPage(cfg: LookupConfig) {
  return function LookupPage({ session }: { session: AuthSession }) {
    return (
      <PersonalizationProvider pageCode={cfg.pageCode} session={session}>
        <LookupInner session={session} cfg={cfg} />
      </PersonalizationProvider>
    );
  };
}

export const SfkDistributionModePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.DISTRIBUTION_MODE', table: 'sfk_distribution_mode', tlTable: 'sfk_distribution_mode_tl',
  codeColumn: 'distribution_mode_code', titleKey: 'sfk.lookup.distribution_mode.title', titleDefault: 'Distribution Modes',
});
export const SfkAppUserTypePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.APP_USER_TYPE', table: 'sfk_app_user_type', tlTable: 'sfk_app_user_type_tl',
  codeColumn: 'app_user_type_code', titleKey: 'sfk.lookup.subject_type.title', titleDefault: 'Subject Types',
});
export const SfkConnectionStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.CONNECTION_STATUS', table: 'sfk_connection_status', tlTable: 'sfk_connection_status_tl',
  codeColumn: 'connection_status_code', titleKey: 'sfk.lookup.connection_status.title', titleDefault: 'Connection Statuses',
});
export const SfkOrgNodeTypePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.ORG_NODE_TYPE', table: 'sfk_org_node_type', tlTable: 'sfk_org_node_type_tl',
  codeColumn: 'org_node_type_code', titleKey: 'sfk.lookup.org_node_type.title', titleDefault: 'Org Node Types',
});

// ── Phase 2 lookups (ingestion / intake / reconciliation) ──
export const SfkRunTypePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.RUN_TYPE', table: 'sfk_run_type', tlTable: 'sfk_run_type_tl',
  codeColumn: 'run_type_code', titleKey: 'sfk.lookup.run_type.title', titleDefault: 'Ingestion Run Types',
});
export const SfkTrackingTokenPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.TRACKING_TOKEN', table: 'sfk_tracking_token', tlTable: 'sfk_tracking_token_tl',
  codeColumn: 'tracking_token_code', titleKey: 'sfk.lookup.tracking_token.title', titleDefault: 'Tracking Tokens',
});
export const SfkVarianceTypePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.VARIANCE_TYPE', table: 'sfk_variance_type', tlTable: 'sfk_variance_type_tl',
  codeColumn: 'variance_type_code', titleKey: 'sfk.lookup.variance_type.title', titleDefault: 'Variance Types',
});
export const SfkVarianceDispositionPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.VARIANCE_DISPOSITION', table: 'sfk_variance_disposition', tlTable: 'sfk_variance_disposition_tl',
  codeColumn: 'variance_disposition_code', titleKey: 'sfk.lookup.variance_disposition.title', titleDefault: 'Variance Dispositions',
});
export const SfkIntakeTypePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.INTAKE_TYPE', table: 'sfk_intake_type', tlTable: 'sfk_intake_type_tl',
  codeColumn: 'intake_type_code', titleKey: 'sfk.lookup.intake_type.title', titleDefault: 'Intake Types',
});
export const SfkIntakeStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.INTAKE_STATUS', table: 'sfk_intake_status', tlTable: 'sfk_intake_status_tl',
  codeColumn: 'intake_status_code', titleKey: 'sfk.lookup.intake_status.title', titleDefault: 'Intake Statuses',
});
export const SfkIntakeReasonCodePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.INTAKE_REASON_CODE', table: 'sfk_intake_reason', tlTable: 'sfk_intake_reason_tl',
  codeColumn: 'intake_reason_code', titleKey: 'sfk.lookup.intake_reason_code.title', titleDefault: 'Intake Reason Codes',
});

// ── Phase 3 lookups (evaluation engines) ──
export const SfkRuleSeverityPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.RULE_SEVERITY', table: 'sfk_rule_severity', tlTable: 'sfk_rule_severity_tl',
  codeColumn: 'rule_severity_code', titleKey: 'sfk.lookup.rule_severity.title', titleDefault: 'Rule Severities',
});
export const SfkRuleOpStatePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.RULE_OP_STATE', table: 'sfk_rule_op_state', tlTable: 'sfk_rule_op_state_tl',
  codeColumn: 'rule_op_state_code', titleKey: 'sfk.lookup.rule_op_state.title', titleDefault: 'Rule Operational States',
});
export const SfkContinuityStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.CONTINUITY_STATUS', table: 'sfk_continuity_status', tlTable: 'sfk_continuity_status_tl',
  codeColumn: 'continuity_status_code', titleKey: 'sfk.lookup.continuity_status.title', titleDefault: 'Continuity Statuses',
});

// ── Phase 4 lookups (breach lifecycle) ──
export const SfkViolationStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.VIOLATION_STATUS', table: 'sfk_violation_status', tlTable: 'sfk_violation_status_tl',
  codeColumn: 'violation_status_code', titleKey: 'sfk.lookup.violation_status.title', titleDefault: 'Violation Statuses',
});
export const SfkReasonCodePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.REASON_CODE', table: 'sfk_reason', tlTable: 'sfk_reason_tl',
  codeColumn: 'reason_code', titleKey: 'sfk.lookup.reason_code.title', titleDefault: 'Exception Reason Codes',
});
export const SfkMitigatingActionCodePage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.MITIGATING_ACTION_CODE', table: 'sfk_mitigating_action', tlTable: 'sfk_mitigating_action_tl',
  codeColumn: 'mitigating_action_code', titleKey: 'sfk.lookup.mitigating_action_code.title', titleDefault: 'Mitigating Action Codes',
});

// ── Phase 5 lookups (sandbox / remediation) ──
export const SfkSandboxStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.SANDBOX_STATUS', table: 'sfk_sandbox_status', tlTable: 'sfk_sandbox_status_tl',
  codeColumn: 'sandbox_status_code', titleKey: 'sfk.lookup.sandbox_status.title', titleDefault: 'Sandbox Statuses',
});
export const SfkSandboxOperationPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.SANDBOX_OPERATION', table: 'sfk_sandbox_operation', tlTable: 'sfk_sandbox_operation_tl',
  codeColumn: 'sandbox_operation_code', titleKey: 'sfk.lookup.sandbox_operation.title', titleDefault: 'Sandbox Operations',
});
export const SfkSandboxDispositionPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.SANDBOX_DISPOSITION', table: 'sfk_sandbox_disposition', tlTable: 'sfk_sandbox_disposition_tl',
  codeColumn: 'sandbox_disposition_code', titleKey: 'sfk.lookup.sandbox_disposition.title', titleDefault: 'Sandbox Dispositions',
});

// ── Phase 6 lookups (transaction / alert monitoring) ──
export const SfkTransactionStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.TRANSACTION_STATUS', table: 'sfk_transaction_status', tlTable: 'sfk_transaction_status_tl',
  codeColumn: 'transaction_status_code', titleKey: 'sfk.lookup.transaction_status.title', titleDefault: 'Transaction Statuses',
});
export const SfkTransactionClosureReasonPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.TRANSACTION_CLOSURE_REASON', table: 'sfk_transaction_closure_reason', tlTable: 'sfk_transaction_closure_reason_tl',
  codeColumn: 'transaction_closure_reason_code', titleKey: 'sfk.lookup.transaction_closure_reason.title', titleDefault: 'Transaction Closure Reasons',
});
export const SfkAlertStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.ALERT_STATUS', table: 'sfk_alert_status', tlTable: 'sfk_alert_status_tl',
  codeColumn: 'alert_status_code', titleKey: 'sfk.lookup.alert_status.title', titleDefault: 'Alert Statuses',
});

// ── Phase 7 lookups (release / regulatory / gatekeeper) ──
export const SfkReleaseStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.RELEASE_STATUS', table: 'sfk_release_status', tlTable: 'sfk_release_status_tl',
  codeColumn: 'release_status_code', titleKey: 'sfk.lookup.release_status.title', titleDefault: 'Release Statuses',
});
export const SfkReleaseItemStatusPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.RELEASE_ITEM_STATUS', table: 'sfk_release_item_status', tlTable: 'sfk_release_item_status_tl',
  codeColumn: 'release_item_status_code', titleKey: 'sfk.lookup.release_item_status.title', titleDefault: 'Release Item Statuses',
});
export const SfkRegulatoryFrameworkPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.REGULATORY_FRAMEWORK', table: 'sfk_regulatory_framework', tlTable: 'sfk_regulatory_framework_tl',
  codeColumn: 'framework_code', titleKey: 'sfk.lookup.regulatory_framework.title', titleDefault: 'Regulatory Frameworks',
});
export const SfkGatekeeperDispositionPage = makeLookupPage({
  pageCode: 'SFK.LOOKUP.GATEKEEPER_DISPOSITION', table: 'sfk_gatekeeper_disposition', tlTable: 'sfk_gatekeeper_disposition_tl',
  codeColumn: 'gatekeeper_disposition_code', titleKey: 'sfk.lookup.gatekeeper_disposition.title', titleDefault: 'Gatekeeper Dispositions',
});
